import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { fetchVendors, patchVendor } from '../api/vendors';
import type { Vendor, VendorPatch } from '../api/vendors';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { FilterSelect } from '../components/ui/filter-select';
import { Tooltip } from '../components/ui/tooltip';
import { DataTable } from '../components/data-table/DataTable';
import { ColumnsMenu, FilterBar, FilterRow, PageHeading } from '../components/data-table/TableChrome';
import { cn } from '../lib/utils';

const KIND_LABELS: Record<string, string> = {
  vendor:      'Vendor',
  competitor:  'Competitor',
  price_check: 'Price check',
  FIT:         'FIT',
};

export default function VendorsPage() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [saving, setSaving]   = useState<Record<string, boolean>>({});
  const [kindFilter, setKindFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setVendors(await fetchVendors());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = useCallback(async (vendorCode: string, patch: VendorPatch) => {
    setSaving(s => ({ ...s, [vendorCode]: true }));
    try {
      await patchVendor(vendorCode, patch);
      // optimistic update already applied — no reload needed
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      await load(); // revert on failure
    } finally {
      setSaving(s => ({ ...s, [vendorCode]: false }));
    }
  }, [load]);

  const applyLocal = useCallback((vendorCode: string, patch: VendorPatch) => {
    setVendors(vs => vs.map(v => (v.vendor === vendorCode ? { ...v, ...patch } : v)));
  }, []);

  /** Optimistic local write, then persist. */
  const commit = useCallback(<K extends keyof VendorPatch>(
    vendorCode: string,
    field: K,
    value: VendorPatch[K],
  ) => {
    applyLocal(vendorCode, { [field]: value } as VendorPatch);
    void save(vendorCode, { [field]: value } as VendorPatch);
  }, [applyLocal, save]);

  const kinds = useMemo(() => [...new Set(vendors.map(v => v.kind))].sort(), [vendors]);
  const displayed = useMemo(
    () => (kindFilter ? vendors.filter(v => v.kind === kindFilter) : vendors),
    [vendors, kindFilter],
  );

  const kindOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...kinds.map(k => ({ value: k, label: KIND_LABELS[k] ?? k }))],
    [kinds],
  );

  const columns = useMemo<ColumnDef<Vendor>[]>(() => {
    const toggle = (
      id: string,
      label: string,
      field: 'liveupdate' | 'published' | 'auto_add_new_items' | 'auto_approve_safe' | 'auto_approve_home_value',
    ): ColumnDef<Vendor> => ({
      id,
      meta: { label },
      header: label,
      cell: ({ row }) => (
        <Toggle
          checked={row.original[field]}
          disabled={!!saving[row.original.vendor]}
          onChange={() => commit(row.original.vendor, field, !row.original[field])}
          label={`${label} — ${row.original.vendor}`}
        />
      ),
    });

    return [
      {
        id: 'code',
        size: 90,
        enableHiding: false,
        meta: { label: 'Code' },
        header: 'Code',
        cell: ({ row }) => (
          <span className="font-mono text-[12.5px] font-medium">{row.original.vendor}</span>
        ),
      },
      {
        id: 'name',
        size: 200,
        enableHiding: false,
        meta: { label: 'Name' },
        header: 'Name',
        cell: ({ row }) => <span className="whitespace-nowrap">{row.original.name}</span>,
      },
      {
        id: 'kind',
        meta: { label: 'Kind' },
        header: 'Kind',
        cell: ({ row }) => <Badge variant="neutral">{KIND_LABELS[row.original.kind] ?? row.original.kind}</Badge>,
      },
      toggle('live', 'Live', 'liveupdate'),
      toggle('published', 'Published', 'published'),
      toggle('autoAdd', 'Auto-add new items', 'auto_add_new_items'),
      // Per-vendor gate, AND'd with the global auto-approve setting; both must be
      // on for this vendor's SAFE price checks to auto-approve and push to NS.
      toggle('autoApprove', 'Auto-approve safe', 'auto_approve_safe'),
      // Same two-level gate for Home Value (BOT-411): this toggle AND the global
      // "Auto-approve Home Value" switch. Vendors with no Home Value lane (no
      // availability extractor — CDK price-file vendors, competitors) get a
      // marker instead of a switch: a disabled button would swallow the tooltip.
      {
        id: 'autoApproveHomeValue',
        meta: { label: 'Auto-approve Home Value' },
        header: 'Auto-approve Home Value',
        cell: ({ row }) =>
          row.original.home_value_lane ? (
            <Toggle
              checked={row.original.auto_approve_home_value}
              disabled={!!saving[row.original.vendor]}
              onChange={() =>
                commit(row.original.vendor, 'auto_approve_home_value', !row.original.auto_approve_home_value)
              }
              label={`Auto-approve Home Value — ${row.original.vendor}`}
            />
          ) : (
            <Tooltip label="No Home Value lane for this vendor (no availability extractor)">
              <span aria-label={`No Home Value lane — ${row.original.vendor}`} className="text-xs text-zinc-300">—</span>
            </Tooltip>
          ),
      },
      {
        id: 'dailyLimit',
        meta: { label: 'Daily limit', align: 'right' },
        header: 'Daily limit',
        cell: ({ row }) => (
          <InlineNumber
            value={row.original.daily_limit}
            integer
            min={0}
            onCommit={v => commit(row.original.vendor, 'daily_limit', v)}
          />
        ),
      },
      {
        id: 'threshold',
        meta: { label: 'Threshold (s)', align: 'right' },
        header: 'Threshold (s)',
        cell: ({ row }) => (
          <InlineNumber
            value={row.original.threshold}
            integer
            min={1}
            onCommit={v => commit(row.original.vendor, 'threshold', v)}
          />
        ),
      },
      // SAFE price range (BOT-466) — decision: saferange, see decisions/saferange.md
      {
        id: 'safeDecreasePct',
        meta: { label: 'Safe ↓ %', align: 'right' },
        header: 'Safe ↓ %',
        cell: ({ row }) => (
          <InlineNumber
            label={`Safe decrease % — ${row.original.vendor}`}
            value={row.original.safe_decrease_pct ?? 0}
            min={0}
            max={100}
            step={0.5}
            suffix="%"
            onCommit={v => commit(row.original.vendor, 'safe_decrease_pct', v)}
          />
        ),
      },
      {
        id: 'safePct',
        meta: { label: 'Safe ↑ %', align: 'right' },
        header: 'Safe ↑ %',
        cell: ({ row }) => (
          <InlineNumber
            label={`Safe increase % — ${row.original.vendor}`}
            value={row.original.safe_threshold_pct ?? 0}
            min={0.01}
            max={100}
            step={0.5}
            suffix="%"
            onCommit={v => commit(row.original.vendor, 'safe_threshold_pct', v)}
          />
        ),
      },
      {
        id: 'cutOff',
        meta: { label: 'Cut-off date' },
        header: 'Cut-off date',
        cell: ({ row }) => (
          <InlineDate
            value={row.original.cut_off_date}
            onCommit={v => commit(row.original.vendor, 'cut_off_date', v)}
          />
        ),
      },
      {
        id: 'proxy',
        meta: { label: 'Proxy' },
        header: 'Proxy',
        cell: ({ row }) => (
          <InlineText
            value={row.original.proxy ?? ''}
            placeholder="none"
            className="w-40"
            onCommit={v => commit(row.original.vendor, 'proxy', v || null)}
          />
        ),
      },
      {
        id: 'delay',
        meta: { label: 'Delay' },
        header: 'Delay',
        cell: ({ row }) => (
          <InlineText
            value={row.original.delay ?? ''}
            placeholder="5..15"
            className="w-20"
            onCommit={v => commit(row.original.vendor, 'delay', v || null)}
          />
        ),
      },
      {
        id: 'tasksToAdd',
        meta: { label: 'Tasks to add', align: 'right' },
        header: 'Tasks to add',
        cell: ({ row }) => (
          <InlineNumber
            value={row.original.tasks_to_add}
            integer
            min={0}
            onCommit={v => commit(row.original.vendor, 'tasks_to_add', v)}
          />
        ),
      },
      {
        id: 'note',
        meta: { label: 'Note' },
        header: 'Note',
        cell: ({ row }) => (
          <InlineText
            value={row.original.note ?? ''}
            placeholder="—"
            className="w-56"
            onCommit={v => commit(row.original.vendor, 'note', v || null)}
          />
        ),
      },
    ];
  }, [commit, saving]);

  const table = useReactTable({
    data: displayed,
    columns,
    getRowId: row => row.vendor,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    initialState: { columnPinning: { left: ['code', 'name'] } },
  });

  return (
    <>
      <PageHeading title="Vendors" count={displayed.length} />

      <FilterBar>
        <FilterRow>
          <FilterSelect label="Kind" options={kindOptions} value={kindFilter} onChange={setKindFilter} />
          <div className="ml-auto">
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
          {error}
        </div>
      )}

      {/* mb-5 — see UsersPage: no pagination bar under this table. */}
      <DataTable table={table} loading={loading} emptyMessage="No vendors found" className="mb-5" />
    </>
  );
}

// ── Inline edit primitives ───────────────────────────────────────────────────
//
// Click-to-edit rather than always-on inputs: this table is mostly read, and a
// grid of live inputs reads as a form you are expected to fill in.

function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        checked ? 'bg-primary' : 'bg-border',
      )}
    >
      <span
        className={cn(
          'inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-[18px]' : 'translate-x-[3px]',
        )}
      />
    </button>
  );
}

function InlineText({
  value,
  onCommit,
  placeholder,
  className,
}: {
  value: string;
  onCommit: (val: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);

  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  function commit() {
    setEditing(false);
    if (draft !== value) onCommit(draft);
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title={draft || placeholder}
        className={cn('block max-w-[240px] truncate rounded-sm px-1 py-0.5 text-left hover:bg-accent', className)}
      >
        {draft || <span className="text-zinc-300">{placeholder}</span>}
      </button>
    );
  }

  return (
    <Input
      autoFocus
      type="text"
      value={draft}
      placeholder={placeholder}
      className={cn('h-7', className)}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { setDraft(value); setEditing(false); }
      }}
    />
  );
}

function InlineNumber({
  value,
  onCommit,
  min,
  max,
  step,
  integer,
  suffix,
  label,
}: {
  value: number;
  onCommit: (val: number) => void;
  min?: number;
  max?: number;
  step?: number;
  integer?: boolean;
  suffix?: string;
  label?: string;
}) {
  const [draft, setDraft] = useState(String(value));
  const [editing, setEditing] = useState(false);

  useEffect(() => { if (!editing) setDraft(String(value)); }, [value, editing]);

  function commit() {
    setEditing(false);
    const n = integer ? parseInt(draft, 10) : parseFloat(draft);
    if (!isNaN(n) && n !== value) onCommit(n);
    else setDraft(String(value));
  }

  if (!editing) {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={() => setEditing(true)}
        className="w-full rounded-sm px-1 py-0.5 text-right tabular-nums hover:bg-accent"
      >
        {integer ? value.toLocaleString('en-US') : value}
        {suffix}
      </button>
    );
  }

  return (
    <Input
      autoFocus
      type="number"
      aria-label={label}
      value={draft}
      min={min}
      max={max}
      step={step ?? (integer ? 1 : 0.1)}
      className="h-7 w-20 text-right tabular-nums"
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { setDraft(String(value)); setEditing(false); }
      }}
    />
  );
}

function InlineDate({ value, onCommit }: { value: string | null; onCommit: (val: string) => void }) {
  // Date portion only; the API keeps whatever time component it needs.
  const dateStr = value ? value.slice(0, 10) : '';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(dateStr);

  useEffect(() => { if (!editing) setDraft(value ? value.slice(0, 10) : ''); }, [value, editing]);

  function commit() {
    setEditing(false);
    if (draft && draft !== dateStr) onCommit(draft);
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="whitespace-nowrap rounded-sm px-1 py-0.5 hover:bg-accent"
      >
        {dateStr || <span className="text-zinc-300">—</span>}
      </button>
    );
  }

  return (
    <Input
      autoFocus
      type="date"
      value={draft}
      className="h-7 w-[140px]"
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { setDraft(dateStr); setEditing(false); }
      }}
    />
  );
}
